/**
 * Auth store — single source of truth for session state.
 *
 * Persists the user + token to localStorage so the session survives a
 * reload. We deliberately store the token client-side so it can be
 * attached to outbound API calls; project-context §7.6.1 keeps it
 * short-lived and tied to a refresh-token rotation in KV (server-side).
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthSession, AuthUser } from './types'
import { setReporterUser } from '@/shared/lib/error-reporter'
import { mergeAnonymousToServer } from '@/features/saved-items/store'

interface AuthState {
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
  /** True once the persisted store has rehydrated from localStorage. */
  hydrated: boolean
  setSession: (session: AuthSession) => void
  setUser: (user: AuthUser) => void
  /** Update just the tokens after a silent refresh. */
  setTokens: (tokens: { token: string; refreshToken?: string | null }) => void
  clear: () => void
  setHydrated: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      hydrated: false,
      setSession: ({ user, token, refreshToken }) => {
        set({ user, token, refreshToken: refreshToken ?? null })
        setReporterUser(user.id)
        // Merge any anonymous saved items to the server after login.
        void mergeAnonymousToServer()
      },
      setUser: (user) => {
        set({ user })
        setReporterUser(user.id)
      },
      setTokens: ({ token, refreshToken }) =>
        set((s) => ({
          token,
          refreshToken: refreshToken ?? s.refreshToken,
        })),
      clear: () => {
        set({ user: null, token: null, refreshToken: null })
        setReporterUser(null)
      },
      setHydrated: () => set({ hydrated: true }),
    }),
    {
      name: 'abdallah-auth',
      partialize: (s) => ({ user: s.user, token: s.token, refreshToken: s.refreshToken }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated()
        if (state?.user?.id) setReporterUser(state.user.id)
      },
    },
  ),
)

export const useIsAuthenticated = () =>
  useAuthStore((s) => Boolean(s.user && s.token))

export const useCurrentUser = () => useAuthStore((s) => s.user)

export const useAuthHydrated = () => useAuthStore((s) => s.hydrated)
